import apiClient from '../client';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';
import type {
  RiskAssessmentResponse,
  RiskAssessmentListResponse,
  CreateRiskAssessmentRequest,
  RiskStatistics,
  RiskLevel,
} from '@/types/risk.types';

export const riskApi = {
  getById: async (id: string): Promise<RiskAssessmentResponse> => {
    const response = await apiClient.get<RiskAssessmentResponse>(`/v1/risk-assessments/${id}`);
    return response.data;
  },

  getByPatient: async (
    patientId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<RiskAssessmentListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<RiskAssessmentListResponse>>(
      `/v1/risk-assessments/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  getHistoryByPatient: async (patientId: string): Promise<RiskAssessmentListResponse[]> => {
    const response = await apiClient.get<RiskAssessmentListResponse[]>(
      `/v1/risk-assessments/patient/${patientId}/history`
    );
    return response.data;
  },

  getCurrentByPatient: async (patientId: string): Promise<RiskAssessmentResponse | null> => {
    try {
      const response = await apiClient.get<RiskAssessmentResponse>(
        `/v1/risk-assessments/patient/${patientId}/current`
      );
      return response.data;
    } catch {
      return null;
    }
  },

  getLatestByPatient: async (patientId: string): Promise<RiskAssessmentResponse | null> => {
    try {
      const response = await apiClient.get<RiskAssessmentResponse>(
        `/v1/risk-assessments/patient/${patientId}/latest`
      );
      return response.data;
    } catch {
      return null;
    }
  },

  getByRiskLevel: async (level: RiskLevel): Promise<RiskAssessmentListResponse[]> => {
    const response = await apiClient.get<RiskAssessmentListResponse[]>(
      `/v1/risk-assessments/level/${level}`
    );
    return response.data;
  },

  getHighRiskPatients: async (): Promise<RiskAssessmentListResponse[]> => {
    const response = await apiClient.get<RiskAssessmentListResponse[]>(
      '/v1/risk-assessments/high-risk'
    );
    return response.data;
  },

  getExpiringSoon: async (daysAhead: number = 7): Promise<RiskAssessmentListResponse[]> => {
    const response = await apiClient.get<RiskAssessmentListResponse[]>(
      '/v1/risk-assessments/expiring',
      { params: { daysAhead } }
    );
    return response.data;
  },

  getStatistics: async (): Promise<RiskStatistics> => {
    const response = await apiClient.get<RiskStatistics>('/v1/risk-assessments/statistics');
    return response.data;
  },

  create: async (data: CreateRiskAssessmentRequest): Promise<RiskAssessmentResponse> => {
    const response = await apiClient.post<RiskAssessmentResponse>('/v1/risk-assessments', data);
    return response.data;
  },
};
